import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Help Center Page
 * Educational investment articles in English and Turkish
 * Provides guides on trading basics, technical analysis, and risk management
 */
import { useState, useMemo } from 'react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { 
  HelpCircle, Search, Book, TrendingUp, BarChart2, Shield, 
  BookOpen, ChevronRight, ArrowLeft, ThumbsUp, ThumbsDown,
  Lightbulb, Target, PieChart, AlertTriangle, Wallet, Globe,
  Phone, X, Clock, User, MessageSquare, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Article data - content for both languages
const getArticles = (language) => {
  const articlesData = {
    en: {
      gettingStarted: [
        {
          id: 'gs-1',
          title: 'What is Forex Trading?',
          icon: Globe,
          summary: 'Learn the basics of foreign exchange trading and how the forex market works.',
          content: `
**Introduction to Forex Trading**

Foreign Exchange (Forex) trading is the buying and selling of currencies on the global market. It's the largest financial market in the world, with over $6 trillion traded daily.

**How Does Forex Work?**

Currencies are traded in pairs (e.g., EUR/USD). When you buy EUR/USD, you're buying Euros and selling US Dollars. The exchange rate tells you how much of the quote currency you need to buy one unit of the base currency.

**Key Concepts:**
- **Base Currency**: The first currency in a pair (EUR in EUR/USD)
- **Quote Currency**: The second currency in a pair (USD in EUR/USD)
- **Pip**: The smallest price move in a currency pair (usually 0.0001)
- **Spread**: The difference between buy and sell prices

**Why Trade Forex?**
- 24-hour market, 5 days a week
- High liquidity
- Low transaction costs
- Leverage available
- Trade in both rising and falling markets
          `
        },
        {
          id: 'gs-2',
          title: 'Opening Your First Trade',
          icon: TrendingUp,
          summary: 'A step-by-step guide to placing your first trade on the platform.',
          content: `
**Your First Trade Guide**

Ready to place your first trade? Follow these simple steps:

**Step 1: Choose Your Instrument**
Select a currency pair or asset from the watchlist. Start with major pairs like EUR/USD or GBP/USD as they have lower spreads.

**Step 2: Analyze the Market**
- Check the current price trend
- Review the chart for support and resistance levels
- Consider any upcoming economic events

**Step 3: Decide Your Position**
- **Buy (Long)**: If you think the price will go UP
- **Sell (Short)**: If you think the price will go DOWN

**Step 4: Set Your Trade Size**
Choose your lot size. Start small while learning. Mini lots (0.01) are perfect for beginners.

**Step 5: Set Stop Loss & Take Profit**
- **Stop Loss**: Automatically closes your trade if the price moves against you
- **Take Profit**: Automatically closes your trade when you reach your target profit

**Step 6: Execute the Trade**
Click Buy or Sell. Your trade is now open! Monitor it in the "Open Positions" panel.
          `
        },
        {
          id: 'gs-3',
          title: 'Understanding Your Dashboard',
          icon: PieChart,
          summary: 'Navigate the trading platform and understand all the tools available.',
          content: `
**Platform Dashboard Guide**

Your trading dashboard has several important sections:

**1. Watchlist Panel**
- Lists all available trading instruments
- Shows real-time bid/ask prices
- Color indicators: Green (price up), Red (price down)

**2. Chart Area**
- Visual representation of price movements
- Multiple timeframes (1M, 5M, 15M, 1H, 4H, 1D)
- Technical indicators available

**3. Trade Panel**
- One-click trading button
- Buy/Sell buttons with current prices
- Position size controls

**4. Positions Panel**
- Open Positions: Your current active trades
- Pending Orders: Orders waiting to be executed
- Closed Positions: Your trade history

**5. Account Summary**
- Balance: Total funds in your account
- Equity: Balance + unrealized profit/loss
- Free Margin: Available funds for new trades
- Used Margin: Funds locked in current positions
          `
        }
      ],
      tradingBasics: [
        {
          id: 'tb-1',
          title: 'Types of Trading Orders',
          icon: Target,
          summary: 'Learn about market orders, limit orders, and stop orders.',
          content: `
**Understanding Order Types**

Different orders help you enter and exit trades at the right time.

**Market Order**
Executes immediately at the current market price. Best for:
- Quick entry when you're confident
- Liquid markets with tight spreads

**Limit Order**
Executes only when price reaches your specified level. Types:
- **Buy Limit**: Below current price - buy at a discount
- **Sell Limit**: Above current price - sell at a premium

**Stop Order**
Triggers when price reaches a certain level:
- **Buy Stop**: Above current price - catches breakouts
- **Sell Stop**: Below current price - catches breakdowns

**Stop Loss Order**
Essential risk management tool. Automatically closes losing positions.

**Take Profit Order**
Automatically closes winning positions at your target price.

**Pro Tips:**
- Always use stop losses
- Set realistic take profit levels
- Consider the spread when placing orders
          `
        },
        {
          id: 'tb-2',
          title: 'What is Leverage?',
          icon: TrendingUp,
          summary: 'Understand leverage, margin, and how to use them responsibly.',
          content: `
**Leverage Explained**

Leverage allows you to control a large position with a small amount of capital.

**How Leverage Works:**
With 1:100 leverage, $1,000 controls a $100,000 position.

**Example:**
- Account Balance: $1,000
- Leverage: 1:100
- Position Size: $100,000
- Price moves 1%: You gain/lose $1,000

**Margin Requirements:**
- **Initial Margin**: Amount needed to open a position
- **Maintenance Margin**: Minimum balance to keep position open
- **Margin Call**: Warning that your margin is low
- **Stop Out**: Positions closed automatically if margin depleted

**Leverage Risks:**
- Amplifies both gains AND losses
- Can lose more than your deposit
- Higher leverage = higher risk

**Responsible Leverage Use:**
- Start with lower leverage (1:10 to 1:30)
- Never risk more than 1-2% per trade
- Always use stop losses
- Monitor margin level constantly
          `
        },
        {
          id: 'tb-3',
          title: 'Understanding Spreads',
          icon: BarChart2,
          summary: 'Learn how spreads work and their impact on your trading costs.',
          content: `
**What is a Spread?**

The spread is the difference between the buy (ask) and sell (bid) price.

**Example:**
- EUR/USD Bid: 1.0850
- EUR/USD Ask: 1.0852
- Spread: 2 pips (0.0002)

**Types of Spreads:**

**Fixed Spreads:**
- Stay constant regardless of market conditions
- Predictable trading costs
- Usually slightly wider than variable spreads

**Variable Spreads:**
- Change based on market conditions
- Tighter during high liquidity periods
- Can widen during news events or low liquidity

**Factors Affecting Spreads:**
- Market volatility
- Liquidity
- Time of day
- Economic events
- Instrument type

**Minimizing Spread Costs:**
- Trade during peak hours (London/New York overlap)
- Avoid trading during major news releases
- Choose major currency pairs (tighter spreads)
- Consider your broker's spread type
          `
        }
      ],
      technicalAnalysis: [
        {
          id: 'ta-1',
          title: 'Support and Resistance',
          icon: BarChart2,
          summary: 'Identify key price levels where markets tend to reverse.',
          content: `
**Support and Resistance Basics**

These are key price levels where supply and demand meet.

**Support Level:**
A price level where buying pressure prevents further decline.
- Acts as a "floor"
- Buyers step in at these levels
- The more times tested, the stronger it becomes

**Resistance Level:**
A price level where selling pressure prevents further rise.
- Acts as a "ceiling"
- Sellers appear at these levels
- Often becomes support once broken

**How to Identify:**
1. Look for areas where price reversed multiple times
2. Round numbers often act as psychological levels
3. Previous highs and lows are important
4. Moving averages can act as dynamic S/R

**Trading Strategies:**
- **Bounce Trading**: Buy at support, sell at resistance
- **Breakout Trading**: Trade when price breaks through levels
- **Pullback Trading**: Wait for retest after breakout

**Remember:**
Support and resistance are zones, not exact prices. Always use a buffer.
          `
        },
        {
          id: 'ta-2',
          title: 'Trend Analysis',
          icon: TrendingUp,
          summary: 'Learn to identify uptrends, downtrends, and sideways markets.',
          content: `
**Understanding Market Trends**

"The trend is your friend" - a fundamental trading principle.

**Types of Trends:**

**Uptrend:**
- Higher highs and higher lows
- Buyers are in control
- Look for buying opportunities

**Downtrend:**
- Lower highs and lower lows
- Sellers are in control
- Look for selling opportunities

**Sideways/Range:**
- Price moves between support and resistance
- No clear direction
- Trade the range or wait for breakout

**Identifying Trends:**

**Visual Analysis:**
Connect swing highs and lows with trendlines

**Moving Averages:**
- Price above MA = uptrend
- Price below MA = downtrend
- 50 and 200 MA crossovers signal trend changes

**Trend Strength:**
- Steeper angle = stronger trend
- Wide spacing of highs/lows = strong momentum
- Frequent pullbacks = weaker trend

**Trading With the Trend:**
- Don't fight the trend
- Look for pullbacks to enter
- Place stops beyond recent swing points
          `
        },
        {
          id: 'ta-3',
          title: 'Candlestick Patterns',
          icon: BarChart2,
          summary: 'Read candlestick charts and recognize important patterns.',
          content: `
**Candlestick Analysis**

Japanese candlesticks show price action in a visual format.

**Anatomy of a Candlestick:**
- **Body**: Area between open and close
- **Wick/Shadow**: Lines showing high and low
- **Green/White**: Close higher than open (bullish)
- **Red/Black**: Close lower than open (bearish)

**Single Candle Patterns:**

**Doji:**
Open and close are nearly equal. Shows indecision.

**Hammer/Hanging Man:**
Small body with long lower wick. Reversal signal.

**Shooting Star:**
Small body with long upper wick. Bearish reversal.

**Multi-Candle Patterns:**

**Engulfing:**
Second candle completely covers first. Strong reversal signal.

**Morning/Evening Star:**
Three candles showing reversal at key levels.

**Three White Soldiers/Black Crows:**
Three consecutive strong candles. Trend confirmation.

**Using Patterns:**
- Look for patterns at support/resistance
- Combine with other indicators
- Wait for confirmation
- Patterns work better on higher timeframes
          `
        }
      ],
      riskManagement: [
        {
          id: 'rm-1',
          title: 'Position Sizing',
          icon: Target,
          summary: 'Calculate the right trade size based on your risk tolerance.',
          content: `
**Position Sizing Guide**

Never risk more than you can afford to lose.

**The 1-2% Rule:**
Never risk more than 1-2% of your account on a single trade.

**Calculation Formula:**
Position Size = (Account Risk) / (Trade Risk)

**Example:**
- Account: $10,000
- Risk per trade: 1% = $100
- Stop loss: 50 pips
- Pip value (standard lot): $10/pip

Position Size = $100 / (50 pips × $10) = 0.2 lots

**Benefits of Proper Sizing:**
- Survive losing streaks
- Remove emotional decisions
- Consistent risk exposure
- Long-term account growth

**Position Sizing Tips:**
- Calculate size BEFORE entering trade
- Adjust size based on volatility
- Reduce size during losing streaks
- Never double down on losers
          `
        },
        {
          id: 'rm-2',
          title: 'Risk-Reward Ratio',
          icon: AlertTriangle,
          summary: 'Why your reward should always exceed your risk.',
          content: `
**Understanding Risk-Reward**

The risk-reward ratio compares potential loss to potential profit.

**Calculating Risk-Reward:**
Risk: Distance from entry to stop loss
Reward: Distance from entry to take profit

**Example:**
- Entry: $100
- Stop Loss: $95 (Risk: $5)
- Take Profit: $115 (Reward: $15)
- Risk-Reward: 1:3

**Why It Matters:**
With a 1:2 risk-reward:
- Win 40% of trades and still profit
- Reduces pressure of needing high win rate
- Mathematically sustainable

**Minimum Recommended Ratios:**
- Day Trading: 1:1.5 minimum
- Swing Trading: 1:2 minimum
- Position Trading: 1:3 or higher

**Improving Your Ratio:**
- Enter at better prices (limits, pullbacks)
- Tighten stops with price action
- Let winners run (trailing stops)
- Cut losers quickly
          `
        },
        {
          id: 'rm-3',
          title: 'Trading Psychology',
          icon: Lightbulb,
          summary: 'Master your emotions and develop a winning mindset.',
          content: `
**Trading Psychology**

Your mindset is as important as your strategy.

**Common Emotional Traps:**

**Fear:**
- Afraid to take valid setups
- Cutting winners too early
- Solution: Trust your analysis

**Greed:**
- Overtrading
- Moving stop losses
- Solution: Stick to your plan

**Revenge Trading:**
- Trading to recover losses
- Ignoring rules
- Solution: Walk away, come back fresh

**Overconfidence:**
- Increasing position size after wins
- Ignoring risk management
- Solution: Stay consistent

**Building Discipline:**
1. Create a trading plan and follow it
2. Keep a trading journal
3. Review trades weekly
4. Accept losses as part of trading
5. Focus on process, not profits

**Daily Habits:**
- Start with a clear mind
- Set maximum daily loss limit
- Take breaks during volatile periods
- End day with journal entry
          `
        }
      ],
      glossary: [
        {
          id: 'gl-1',
          title: 'Forex Glossary A-L',
          icon: BookOpen,
          summary: 'Essential trading terms and definitions (A-L).',
          content: `
**Trading Terms A-L**

**Ask Price**: The price at which you can buy an instrument.

**Base Currency**: The first currency in a pair (EUR in EUR/USD).

**Bear Market**: A market trending downward.

**Bid Price**: The price at which you can sell an instrument.

**Bull Market**: A market trending upward.

**Candlestick**: A chart element showing open, close, high, and low prices.

**Day Trading**: Opening and closing trades within the same day.

**Drawdown**: The decline from peak to trough in account value.

**Equity**: Account balance plus/minus unrealized profits/losses.

**Fill**: When your order is executed.

**Gap**: A break in price with no trading activity.

**Hedge**: A position taken to offset potential losses.

**Indicator**: A mathematical calculation displayed on charts.

**Leverage**: Using borrowed capital to increase position size.

**Limit Order**: An order to buy/sell at a specified price or better.

**Liquidity**: How easily an asset can be bought or sold.

**Long**: Buying with expectation of price increase.

**Lot**: A standardized trading unit (100,000 units in forex).
          `
        },
        {
          id: 'gl-2',
          title: 'Forex Glossary M-Z',
          icon: BookOpen,
          summary: 'Essential trading terms and definitions (M-Z).',
          content: `
**Trading Terms M-Z**

**Margin**: Collateral required to open and maintain positions.

**Market Order**: An order executed immediately at current price.

**Moving Average**: An indicator showing average price over time.

**Pip**: The smallest price increment (usually 0.0001).

**Position**: An active trade in the market.

**Quote Currency**: The second currency in a pair (USD in EUR/USD).

**Range**: Price area bounded by support and resistance.

**Resistance**: Price level where selling pressure appears.

**Risk-Reward Ratio**: Potential profit compared to potential loss.

**Scalping**: Taking many small profits on short-term trades.

**Short**: Selling with expectation of price decrease.

**Slippage**: Difference between expected and actual fill price.

**Spread**: Difference between bid and ask prices.

**Stop Loss**: Order to close position at a specified loss level.

**Support**: Price level where buying pressure appears.

**Take Profit**: Order to close position at a specified profit level.

**Trend**: The overall direction of price movement.

**Volatility**: The degree of price fluctuation.

**Volume**: The amount of trading activity.
          `
        }
      ]
    },
    tr: {
      gettingStarted: [
        {
          id: 'gs-1',
          title: 'Forex İşlemi Nedir?',
          icon: Globe,
          summary: 'Döviz ticaretinin temellerini ve forex piyasasının nasıl çalıştığını öğrenin.',
          content: `
**Forex İşlemine Giriş**

Döviz (Forex) ticareti, küresel piyasada döviz alım satımıdır. Günlük 6 trilyon doların üzerinde işlem hacmiyle dünyanın en büyük finansal piyasasıdır.

**Forex Nasıl Çalışır?**

Dövizler çiftler halinde işlem görür (örn. EUR/USD). EUR/USD aldığınızda, Euro alıp ABD Doları satmış olursunuz. Döviz kuru, baz para biriminden bir birim almak için ne kadar karşıt para birimine ihtiyacınız olduğunu söyler.

**Temel Kavramlar:**
- **Baz Döviz**: Çiftteki ilk para birimi (EUR/USD'de EUR)
- **Karşıt Döviz**: Çiftteki ikinci para birimi (EUR/USD'de USD)
- **Pip**: Döviz çiftindeki en küçük fiyat hareketi (genellikle 0.0001)
- **Spread**: Alış ve satış fiyatları arasındaki fark

**Neden Forex İşlemi Yapmalı?**
- Haftada 5 gün, 24 saat açık piyasa
- Yüksek likidite
- Düşük işlem maliyetleri
- Kaldıraç imkanı
- Hem yükselen hem düşen piyasalarda işlem
          `
        },
        {
          id: 'gs-2',
          title: 'İlk İşleminizi Açma',
          icon: TrendingUp,
          summary: 'Platformda ilk işleminizi gerçekleştirmek için adım adım kılavuz.',
          content: `
**İlk İşlem Kılavuzunuz**

İlk işleminizi yapmaya hazır mısınız? Bu basit adımları izleyin:

**Adım 1: Enstrümanınızı Seçin**
İzleme listesinden bir döviz çifti veya varlık seçin. EUR/USD veya GBP/USD gibi majör çiftlerle başlayın çünkü spread'leri daha düşüktür.

**Adım 2: Piyasayı Analiz Edin**
- Mevcut fiyat trendini kontrol edin
- Destek ve direnç seviyeleri için grafiği inceleyin
- Yaklaşan ekonomik olayları göz önünde bulundurun

**Adım 3: Pozisyonunuza Karar Verin**
- **Al (Long)**: Fiyatın YUKARI gideceğini düşünüyorsanız
- **Sat (Short)**: Fiyatın AŞAĞI gideceğini düşünüyorsanız

**Adım 4: İşlem Büyüklüğünüzü Belirleyin**
Lot büyüklüğünüzü seçin. Öğrenirken küçük başlayın. Mini lotlar (0.01) yeni başlayanlar için idealdir.

**Adım 5: Zarar Durdur ve Kar Al Ayarlayın**
- **Zarar Durdur**: Fiyat aleyhinize hareket ederse işleminizi otomatik kapatır
- **Kar Al**: Hedef kârınıza ulaştığınızda işleminizi otomatik kapatır

**Adım 6: İşlemi Gerçekleştirin**
Al veya Sat'a tıklayın. İşleminiz artık açık! "Açık Pozisyonlar" panelinde izleyin.
          `
        },
        {
          id: 'gs-3',
          title: 'Kontrol Panelinizi Anlama',
          icon: PieChart,
          summary: 'İşlem platformunda gezinin ve mevcut tüm araçları anlayın.',
          content: `
**Platform Kontrol Paneli Kılavuzu**

İşlem kontrol panelinizin birkaç önemli bölümü vardır:

**1. İzleme Listesi Paneli**
- Mevcut tüm işlem enstrümanlarını listeler
- Gerçek zamanlı alış/satış fiyatlarını gösterir
- Renk göstergeleri: Yeşil (fiyat yukarı), Kırmızı (fiyat aşağı)

**2. Grafik Alanı**
- Fiyat hareketlerinin görsel gösterimi
- Birden fazla zaman dilimi (1D, 5D, 15D, 1S, 4S, 1G)
- Teknik göstergeler mevcut

**3. İşlem Paneli**
- Tek tıkla işlem butonu
- Güncel fiyatlarla Al/Sat butonları
- Pozisyon büyüklüğü kontrolleri

**4. Pozisyonlar Paneli**
- Açık Pozisyonlar: Mevcut aktif işlemleriniz
- Bekleyen Emirler: Gerçekleşmeyi bekleyen emirler
- Kapalı Pozisyonlar: İşlem geçmişiniz

**5. Hesap Özeti**
- Bakiye: Hesabınızdaki toplam fon
- Özkaynak: Bakiye + gerçekleşmemiş kâr/zarar
- Serbest Marjin: Yeni işlemler için kullanılabilir fonlar
- Kullanılan Marjin: Mevcut pozisyonlarda kilitli fonlar
          `
        }
      ],
      tradingBasics: [
        {
          id: 'tb-1',
          title: 'İşlem Emir Türleri',
          icon: Target,
          summary: 'Piyasa emirleri, limit emirleri ve stop emirleri hakkında bilgi edinin.',
          content: `
**Emir Türlerini Anlama**

Farklı emirler, doğru zamanda işlemlere girip çıkmanıza yardımcı olur.

**Piyasa Emri**
Mevcut piyasa fiyatından anında gerçekleşir. En iyi kullanım:
- Emin olduğunuzda hızlı giriş
- Dar spread'li likit piyasalar

**Limit Emri**
Yalnızca fiyat belirlediğiniz seviyeye ulaştığında gerçekleşir. Türleri:
- **Alış Limiti**: Mevcut fiyatın altında - indirimli al
- **Satış Limiti**: Mevcut fiyatın üstünde - primli sat

**Stop Emri**
Fiyat belirli bir seviyeye ulaştığında tetiklenir:
- **Alış Stop**: Mevcut fiyatın üstünde - kırılımları yakalar
- **Satış Stop**: Mevcut fiyatın altında - düşüşleri yakalar

**Zarar Durdur Emri**
Temel risk yönetimi aracı. Kaybeden pozisyonları otomatik kapatır.

**Kar Al Emri**
Kazanan pozisyonları hedef fiyatınızda otomatik kapatır.

**İpuçları:**
- Her zaman zarar durdur kullanın
- Gerçekçi kar al seviyeleri belirleyin
- Emir verirken spread'i göz önünde bulundurun
          `
        },
        {
          id: 'tb-2',
          title: 'Kaldıraç Nedir?',
          icon: TrendingUp,
          summary: 'Kaldıracı, marjini anlayın ve bunları sorumlu bir şekilde nasıl kullanacağınızı öğrenin.',
          content: `
**Kaldıraç Açıklaması**

Kaldıraç, küçük miktarda sermaye ile büyük pozisyonları kontrol etmenizi sağlar.

**Kaldıraç Nasıl Çalışır:**
1:100 kaldıraç ile 1.000 TL, 100.000 TL'lik pozisyonu kontrol eder.

**Örnek:**
- Hesap Bakiyesi: 1.000 TL
- Kaldıraç: 1:100
- Pozisyon Büyüklüğü: 100.000 TL
- Fiyat %1 hareket eder: 1.000 TL kazanır/kaybedersiniz

**Marjin Gereksinimleri:**
- **Başlangıç Marjini**: Pozisyon açmak için gereken miktar
- **Sürdürme Marjini**: Pozisyonu açık tutmak için minimum bakiye
- **Marjin Çağrısı**: Marjininizin düşük olduğuna dair uyarı
- **Zorunlu Kapanış**: Marjin tükenirse pozisyonlar otomatik kapatılır

**Kaldıraç Riskleri:**
- Hem kazançları HEM DE kayıpları büyütür
- Yatırdığınızdan fazlasını kaybedebilirsiniz
- Yüksek kaldıraç = yüksek risk

**Sorumlu Kaldıraç Kullanımı:**
- Düşük kaldıraçla başlayın (1:10 - 1:30)
- İşlem başına %1-2'den fazla risk almayın
- Her zaman zarar durdur kullanın
- Marjin seviyesini sürekli izleyin
          `
        },
        {
          id: 'tb-3',
          title: 'Spread\'leri Anlama',
          icon: BarChart2,
          summary: 'Spread\'lerin nasıl çalıştığını ve işlem maliyetlerinize etkisini öğrenin.',
          content: `
**Spread Nedir?**

Spread, alış (ask) ve satış (bid) fiyatı arasındaki farktır.

**Örnek:**
- EUR/USD Bid: 1.0850
- EUR/USD Ask: 1.0852
- Spread: 2 pip (0.0002)

**Spread Türleri:**

**Sabit Spread'ler:**
- Piyasa koşullarından bağımsız olarak sabit kalır
- Öngörülebilir işlem maliyetleri
- Genellikle değişken spread'lerden biraz daha geniş

**Değişken Spread'ler:**
- Piyasa koşullarına göre değişir
- Yüksek likidite dönemlerinde daha dar
- Haber dönemlerinde veya düşük likiditede genişleyebilir

**Spread'leri Etkileyen Faktörler:**
- Piyasa oynaklığı
- Likidite
- Günün saati
- Ekonomik olaylar
- Enstrüman türü

**Spread Maliyetlerini Minimize Etme:**
- Yoğun saatlerde işlem yapın (Londra/New York örtüşmesi)
- Önemli haber açıklamalarında işlem yapmaktan kaçının
- Majör döviz çiftlerini seçin (daha dar spread'ler)
- Brokerınızın spread türünü göz önünde bulundurun
          `
        }
      ],
      technicalAnalysis: [
        {
          id: 'ta-1',
          title: 'Destek ve Direnç',
          icon: BarChart2,
          summary: 'Piyasaların tersine dönme eğiliminde olduğu önemli fiyat seviyelerini belirleyin.',
          content: `
**Destek ve Direnç Temelleri**

Bunlar arz ve talebin buluştuğu önemli fiyat seviyeleridir.

**Destek Seviyesi:**
Alım baskısının daha fazla düşüşü önlediği fiyat seviyesi.
- "Taban" görevi görür
- Alıcılar bu seviyelerde devreye girer
- Ne kadar çok test edilirse o kadar güçlenir

**Direnç Seviyesi:**
Satış baskısının daha fazla yükselişi önlediği fiyat seviyesi.
- "Tavan" görevi görür
- Satıcılar bu seviyelerde ortaya çıkar
- Kırıldıktan sonra genellikle destek haline gelir

**Nasıl Belirlenir:**
1. Fiyatın birçok kez tersine döndüğü alanları arayın
2. Yuvarlak sayılar genellikle psikolojik seviye işlevi görür
3. Önceki en yüksek ve en düşük seviyeler önemlidir
4. Hareketli ortalamalar dinamik D/D işlevi görebilir

**İşlem Stratejileri:**
- **Sıçrama İşlemi**: Destekte al, dirençte sat
- **Kırılım İşlemi**: Fiyat seviyeleri kırdığında işlem yap
- **Geri Çekilme İşlemi**: Kırılımdan sonra yeniden test bekle

**Unutmayın:**
Destek ve direnç, kesin fiyatlar değil bölgelerdir. Her zaman tampon kullanın.
          `
        },
        {
          id: 'ta-2',
          title: 'Trend Analizi',
          icon: TrendingUp,
          summary: 'Yükseliş trendlerini, düşüş trendlerini ve yatay piyasaları tanımlamayı öğrenin.',
          content: `
**Piyasa Trendlerini Anlama**

"Trend dostunuzdur" - temel bir işlem prensibi.

**Trend Türleri:**

**Yükseliş Trendi:**
- Daha yüksek tepeler ve daha yüksek dipler
- Alıcılar kontrolde
- Alım fırsatları arayın

**Düşüş Trendi:**
- Daha düşük tepeler ve daha düşük dipler
- Satıcılar kontrolde
- Satış fırsatları arayın

**Yatay/Bant:**
- Fiyat destek ve direnç arasında hareket eder
- Net yön yok
- Bantı işlem yapın veya kırılım bekleyin

**Trendleri Belirleme:**

**Görsel Analiz:**
Trend çizgileri ile salınım tepe ve diplerini bağlayın

**Hareketli Ortalamalar:**
- Fiyat HO üzerinde = yükseliş trendi
- Fiyat HO altında = düşüş trendi
- 50 ve 200 HO kesişmeleri trend değişikliklerini işaret eder

**Trend Gücü:**
- Dik açı = güçlü trend
- Tepe/dip arası geniş boşluk = güçlü momentum
- Sık geri çekilmeler = zayıf trend

**Trendle İşlem:**
- Trende karşı gelmeyin
- Giriş için geri çekilmeleri arayın
- Stopları son salınım noktalarının ötesine koyun
          `
        },
        {
          id: 'ta-3',
          title: 'Mum Çubuğu Formasyonları',
          icon: BarChart2,
          summary: 'Mum çubuğu grafiklerini okuyun ve önemli formasyonları tanıyın.',
          content: `
**Mum Çubuğu Analizi**

Japon mum çubukları fiyat hareketini görsel formatta gösterir.

**Mum Çubuğunun Anatomisi:**
- **Gövde**: Açılış ve kapanış arasındaki alan
- **Fitil/Gölge**: En yüksek ve en düşüğü gösteren çizgiler
- **Yeşil/Beyaz**: Kapanış açılıştan yüksek (boğa)
- **Kırmızı/Siyah**: Kapanış açılıştan düşük (ayı)

**Tek Mum Formasyonları:**

**Doji:**
Açılış ve kapanış neredeyse eşit. Kararsızlık gösterir.

**Çekiç/Asılı Adam:**
Uzun alt fitilli küçük gövde. Dönüş sinyali.

**Kayan Yıldız:**
Uzun üst fitilli küçük gövde. Ayı dönüşü.

**Çoklu Mum Formasyonları:**

**Yutan:**
İkinci mum birincisini tamamen kaplar. Güçlü dönüş sinyali.

**Sabah/Akşam Yıldızı:**
Önemli seviyelerde dönüşü gösteren üç mum.

**Üç Beyaz Asker/Siyah Karga:**
Üç ardışık güçlü mum. Trend onayı.

**Formasyonları Kullanma:**
- Destek/dirençte formasyon arayın
- Diğer göstergelerle birleştirin
- Onay bekleyin
- Formasyonlar yüksek zaman dilimlerinde daha iyi çalışır
          `
        }
      ],
      riskManagement: [
        {
          id: 'rm-1',
          title: 'Pozisyon Boyutlandırma',
          icon: Target,
          summary: 'Risk toleransınıza göre doğru işlem büyüklüğünü hesaplayın.',
          content: `
**Pozisyon Boyutlandırma Kılavuzu**

Kaybetmeyi göze alabileceğinizden fazlasını asla riske atmayın.

**%1-2 Kuralı:**
Tek bir işlemde hesabınızın %1-2'sinden fazlasını asla riske atmayın.

**Hesaplama Formülü:**
Pozisyon Büyüklüğü = (Hesap Riski) / (İşlem Riski)

**Örnek:**
- Hesap: 100.000 TL
- İşlem başına risk: %1 = 1.000 TL
- Zarar durdur: 50 pip
- Pip değeri (standart lot): 100 TL/pip

Pozisyon Büyüklüğü = 1.000 TL / (50 pip × 100 TL) = 0.2 lot

**Doğru Boyutlandırmanın Faydaları:**
- Kayıp serilerinden kurtulma
- Duygusal kararları ortadan kaldırma
- Tutarlı risk maruziyeti
- Uzun vadeli hesap büyümesi

**Pozisyon Boyutlandırma İpuçları:**
- İşleme girmeden ÖNCE boyutu hesaplayın
- Oynaklığa göre boyutu ayarlayın
- Kayıp serilerinde boyutu azaltın
- Kaybedenleri asla ikiye katlamayın
          `
        },
        {
          id: 'rm-2',
          title: 'Risk-Getiri Oranı',
          icon: AlertTriangle,
          summary: 'Getirinizin neden her zaman riskinizi aşması gerektiği.',
          content: `
**Risk-Getiri'yi Anlama**

Risk-getiri oranı, potansiyel kaybı potansiyel kârla karşılaştırır.

**Risk-Getiri Hesaplama:**
Risk: Girişten zarar durdura olan mesafe
Getiri: Girişten kar al'a olan mesafe

**Örnek:**
- Giriş: 100 TL
- Zarar Durdur: 95 TL (Risk: 5 TL)
- Kar Al: 115 TL (Getiri: 15 TL)
- Risk-Getiri: 1:3

**Neden Önemli:**
1:2 risk-getiri ile:
- İşlemlerin %40'ını kazanın ve yine de kâr edin
- Yüksek kazanma oranına ihtiyaç baskısını azaltır
- Matematiksel olarak sürdürülebilir

**Önerilen Minimum Oranlar:**
- Günlük İşlem: Minimum 1:1.5
- Swing İşlem: Minimum 1:2
- Pozisyon İşlemi: 1:3 veya daha yüksek

**Oranınızı İyileştirme:**
- Daha iyi fiyatlardan girin (limitler, geri çekilmeler)
- Fiyat hareketi ile stopları sıkılaştırın
- Kazananların ilerlemesine izin verin (takip eden stoplar)
- Kaybedenleri hızla kesin
          `
        },
        {
          id: 'rm-3',
          title: 'İşlem Psikolojisi',
          icon: Lightbulb,
          summary: 'Duygularınızı kontrol altına alın ve kazanan bir zihniyet geliştirin.',
          content: `
**İşlem Psikolojisi**

Zihniyet, strateji kadar önemlidir.

**Yaygın Duygusal Tuzaklar:**

**Korku:**
- Geçerli kurulumları almaktan korkmak
- Kazananları çok erken kesmek
- Çözüm: Analizinize güvenin

**Açgözlülük:**
- Aşırı işlem
- Zarar durdurları hareket ettirme
- Çözüm: Planınıza sadık kalın

**İntikam İşlemi:**
- Kayıpları telafi etmek için işlem yapma
- Kuralları görmezden gelme
- Çözüm: Uzaklaşın, taze bir kafayla dönün

**Aşırı Güven:**
- Kazançlardan sonra pozisyon büyüklüğünü artırma
- Risk yönetimini görmezden gelme
- Çözüm: Tutarlı kalın

**Disiplin Oluşturma:**
1. İşlem planı oluşturun ve takip edin
2. İşlem günlüğü tutun
3. Haftalık işlemleri gözden geçirin
4. Kayıpları işlemin bir parçası olarak kabul edin
5. Kârlara değil sürece odaklanın

**Günlük Alışkanlıklar:**
- Temiz bir zihinle başlayın
- Maksimum günlük kayıp limiti belirleyin
- Oynak dönemlerde mola verin
- Günü günlük girişi ile bitirin
          `
        }
      ],
      glossary: [
        {
          id: 'gl-1',
          title: 'Forex Sözlüğü A-L',
          icon: BookOpen,
          summary: 'Temel işlem terimleri ve tanımları (A-L).',
          content: `
**İşlem Terimleri A-L**

**Alış Fiyatı (Ask)**: Bir enstrümanı alabileceğiniz fiyat.

**Ayı Piyasası**: Aşağı yönlü trend gösteren piyasa.

**Baz Döviz**: Çiftteki ilk para birimi (EUR/USD'de EUR).

**Boğa Piyasası**: Yukarı yönlü trend gösteren piyasa.

**Düşüş (Drawdown)**: Hesap değerindeki tepeden dibe düşüş.

**Doldurma (Fill)**: Emrinizin gerçekleşmesi.

**Günlük İşlem**: Aynı gün içinde işlem açıp kapatma.

**Gösterge (Indicator)**: Grafiklerde gösterilen matematiksel hesaplama.

**Hedge**: Potansiyel kayıpları dengelemek için alınan pozisyon.

**Kaldıraç**: Pozisyon büyüklüğünü artırmak için ödünç sermaye kullanma.

**Karşıt Döviz**: Çiftteki ikinci para birimi (EUR/USD'de USD).

**Likidite**: Bir varlığın ne kadar kolay alınıp satılabileceği.

**Limit Emri**: Belirli bir fiyattan veya daha iyisinden alma/satma emri.

**Long (Uzun)**: Fiyat artışı beklentisiyle satın alma.

**Lot**: Standartlaştırılmış işlem birimi (forex'te 100.000 birim).

**Mum Çubuğu**: Açılış, kapanış, en yüksek ve en düşük fiyatları gösteren grafik elementi.
          `
        },
        {
          id: 'gl-2',
          title: 'Forex Sözlüğü M-Z',
          icon: BookOpen,
          summary: 'Temel işlem terimleri ve tanımları (M-Z).',
          content: `
**İşlem Terimleri M-Z**

**Marjin**: Pozisyon açmak ve sürdürmek için gereken teminat.

**Mum Çubuğu**: Açılış, kapanış, en yüksek ve en düşük fiyatları gösteren grafik elementi.

**Özkaynak**: Bakiye + gerçekleşmemiş kâr/zarar.

**Pip**: En küçük fiyat artışı (genellikle 0.0001).

**Piyasa Emri**: Mevcut fiyattan hemen gerçekleştirilen emir.

**Pozisyon**: Piyasadaki aktif bir işlem.

**Range (Bant)**: Destek ve dirençle sınırlı fiyat alanı.

**Risk-Getiri Oranı**: Potansiyel kâr ile potansiyel kaybın karşılaştırılması.

**Satış Fiyatı (Bid)**: Bir enstrümanı satabileceğiniz fiyat.

**Short (Kısa)**: Fiyat düşüşü beklentisiyle satış.

**Slippage (Kayma)**: Beklenen ve gerçekleşen doldurma fiyatı arasındaki fark.

**Spread**: Bid ve ask fiyatları arasındaki fark.

**Trend**: Fiyat hareketinin genel yönü.

**Volatilite (Oynaklık)**: Fiyat dalgalanmasının derecesi.

**Volüm (Hacim)**: İşlem aktivitesinin miktarı.

**Zarar Durdur**: Pozisyonu belirli bir kayıp seviyesinde kapatma emri.

**Kar Al**: Pozisyonu belirli bir kâr seviyesinde kapatma emri.

**Destek**: Alım baskısının ortaya çıktığı fiyat seviyesi.

**Direnç**: Satış baskısının ortaya çıktığı fiyat seviyesi.
          `
        }
      ]
    }
  };

  return articlesData[language] || articlesData.en;
};

const TraderHelpCenter = () => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const { t, language } = useTraderLanguage();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [feedback, setFeedback] = useState({});
  
  // Call Me Back modal state
  const [showCallMeModal, setShowCallMeModal] = useState(false);
  const [callMeForm, setCallMeForm] = useState({
    fullName: '',
    phoneNumber: '',
    preferredTime: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Handle Call Me form submission
  const handleCallMeSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('trader_token');
      const response = await fetch(`${API_BASE}/api/trader/support/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...callMeForm,
          language
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(tx(t('helpCenter.requestSuccess')));
        // Reset form after 3 seconds and close modal
        setTimeout(() => {
          setShowCallMeModal(false);
          setSubmitted(false);
          setCallMeForm({
            fullName: '',
            phoneNumber: '',
            preferredTime: '',
            subject: '',
            message: ''
          });
        }, 2000);
      } else {
        toast.error(tx(t('helpCenter.requestError')));
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      toast.error(tx(t('helpCenter.requestError')));
    } finally {
      setSubmitting(false);
    }
  };

  // Call Me Modal Component
  const CallMeModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !submitting && setShowCallMeModal(false)}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl ${
        isDark ? 'bg-[#111827] border border-gray-700' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-blue-100'}`}>
              <Phone size={20} className={isDark ? 'text-cyan-400' : 'text-blue-600'} />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('helpCenter.callMeTitle')}
              </h3>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('helpCenter.callMeSubtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={() => !submitting && setShowCallMeModal(false)}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>
        
        {/* Form */}
        {submitted ? (
          <div className="p-8 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              isDark ? 'bg-green-500/20' : 'bg-green-100'
            }`}>
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tx(language === 'tr' ? 'Talebiniz Alındı!' : 'Request Submitted!')}
            </h4>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('helpCenter.requestSuccess')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleCallMeSubmit} className="p-5 space-y-4">
            {/* Full Name */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <User size={14} className="inline mr-1.5" />
                {t('helpCenter.fullName')} *
              </label>
              <input
                type="text"
                required
                value={callMeForm.fullName}
                onChange={(e) => setCallMeForm(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder={t('helpCenter.fullNamePlaceholder')}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                data-testid="callme-fullname"
              />
            </div>
            
            {/* Phone Number */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <Phone size={14} className="inline mr-1.5" />
                {t('helpCenter.phoneNumber')} *
              </label>
              <input
                type="tel"
                required
                value={callMeForm.phoneNumber}
                onChange={(e) => setCallMeForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder={t('helpCenter.phonePlaceholder')}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                data-testid="callme-phone"
              />
            </div>
            
            {/* Preferred Time */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <Clock size={14} className="inline mr-1.5" />
                {t('helpCenter.preferredTime')} *
              </label>
              <select
                required
                value={callMeForm.preferredTime}
                onChange={(e) => setCallMeForm(prev => ({ ...prev, preferredTime: e.target.value }))}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white focus:border-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                data-testid="callme-time"
              >
                <option value="">{t('helpCenter.selectTime')}</option>
                <option value="morning">{t('helpCenter.morning')}</option>
                <option value="afternoon">{t('helpCenter.afternoon')}</option>
                <option value="evening">{t('helpCenter.evening')}</option>
                <option value="anytime">{t('helpCenter.anytime')}</option>
              </select>
            </div>
            
            {/* Subject */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <HelpCircle size={14} className="inline mr-1.5" />
                {t('helpCenter.subject')} *
              </label>
              <input
                type="text"
                required
                value={callMeForm.subject}
                onChange={(e) => setCallMeForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t('helpCenter.subjectPlaceholder')}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                data-testid="callme-subject"
              />
            </div>
            
            {/* Message (Optional) */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <MessageSquare size={14} className="inline mr-1.5" />
                {t('helpCenter.message')}
              </label>
              <textarea
                rows={3}
                value={callMeForm.message}
                onChange={(e) => setCallMeForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder={t('helpCenter.messagePlaceholder')}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm resize-none ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                data-testid="callme-message"
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                submitting
                  ? 'bg-gray-500 cursor-not-allowed'
                  : isDark 
                    ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              data-testid="callme-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('helpCenter.submitting')}
                </>
              ) : (
                <>
                  <Phone size={18} />
                  {t('helpCenter.submitRequest')}
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  // Get articles based on current language
  const articles = useMemo(() => getArticles(language), [language]);

  // Categories
  const categories = [
    { id: 'all', label: t('helpCenter.allArticles'), icon: Book },
    { id: 'gettingStarted', label: t('helpCenter.gettingStarted'), icon: Lightbulb },
    { id: 'tradingBasics', label: t('helpCenter.tradingBasics'), icon: TrendingUp },
    { id: 'technicalAnalysis', label: t('helpCenter.technicalAnalysis'), icon: BarChart2 },
    { id: 'riskManagement', label: t('helpCenter.riskManagement'), icon: Shield },
    { id: 'glossary', label: t('helpCenter.glossary'), icon: BookOpen }
  ];

  // Get all articles flat
  const allArticles = useMemo(() => {
    const all = [];
    Object.entries(articles).forEach(([category, categoryArticles]) => {
      categoryArticles.forEach(article => {
        all.push({ ...article, category });
      });
    });
    return all;
  }, [articles]);

  // Filter articles
  const filteredArticles = useMemo(() => {
    let filtered = allArticles;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(a => a.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.title.toLowerCase().includes(query) ||
        a.summary.toLowerCase().includes(query) ||
        a.content.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [allArticles, selectedCategory, searchQuery]);

  // Handle feedback
  const handleFeedback = (articleId, helpful) => {
    setFeedback(prev => ({ ...prev, [articleId]: helpful }));
  };

  // Article detail view
  if (selectedArticle) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto p-6">
          {/* Back button */}
          <button
            onClick={() => setSelectedArticle(null)}
            className={`flex items-center gap-2 mb-6 text-sm font-medium ${
              isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-blue-600 hover:text-blue-700'
            }`}
            data-testid="help-back-button"
          >
            <ArrowLeft size={18} />
            {t('helpCenter.backToArticles')}
          </button>

          {/* Article content */}
          <div className={`rounded-xl p-8 ${
            isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-blue-100'}`}>
                <selectedArticle.icon size={28} className={isDark ? 'text-cyan-400' : 'text-blue-600'} />
              </div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {tx(selectedArticle.title)}
              </h1>
            </div>

            <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
              {selectedArticle.content.split('\n').map((line, i) => {
                if (line.startsWith('**') && line.endsWith('**')) {
                  return (
                    <h3 key={i} className={`text-lg font-semibold mt-6 mb-3 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`}>
                      {tx(line.replace(/\*\*/g, ''))}
                    </h3>
                  );
                } else if (line.startsWith('- **')) {
                  const parts = line.replace('- **', '').split('**:');
                  return (
                    <div key={i} className={`flex gap-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="text-cyan-500">•</span>
                      <span>
                        <strong className={isDark ? 'text-white' : 'text-gray-900'}>{parts[0]}:</strong>
                        {parts[1]}
                      </span>
                    </div>
                  );
                } else if (line.startsWith('- ')) {
                  return (
                    <div key={i} className={`flex gap-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="text-cyan-500">•</span>
                      <span>{line.substring(2)}</span>
                    </div>
                  );
                } else if (line.trim()) {
                  return (
                    <p key={i} className={`my-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {line}
                    </p>
                  );
                }
                return null;
              })}
            </div>

            {/* Feedback */}
            <div className={`mt-8 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('helpCenter.wasHelpful')}
                </span>
                <button
                  onClick={() => handleFeedback(selectedArticle.id, true)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                    feedback[selectedArticle.id] === true
                      ? 'bg-green-500/20 text-green-400'
                      : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="help-feedback-yes"
                >
                  <ThumbsUp size={14} />
                  {t('helpCenter.yes')}
                </button>
                <button
                  onClick={() => handleFeedback(selectedArticle.id, false)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                    feedback[selectedArticle.id] === false
                      ? 'bg-red-500/20 text-red-400'
                      : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="help-feedback-no"
                >
                  <ThumbsDown size={14} />
                  {t('helpCenter.no')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Articles list view
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="text-cyan-500" size={32} />
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('helpCenter.title')}
            </h1>
          </div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            {t('helpCenter.subtitle')}
          </p>
        </div>

        {/* Search */}
        <div className={`relative mb-6`}>
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('helpCenter.searchPlaceholder')}
            className={`w-full pl-12 pr-4 py-3 rounded-xl border ${
              isDark 
                ? 'bg-[#111827] border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500' 
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
            data-testid="help-search-input"
          />
        </div>

        <div className="flex gap-6">
          {/* Categories Sidebar */}
          <div className={`w-64 shrink-0 rounded-xl p-4 h-fit ${
            isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('helpCenter.categories')}
            </h3>
            <nav className="space-y-1">
              {categories.map(cat => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-50 text-blue-600'
                        : isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`help-category-${cat.id}`}
                  >
                    <Icon size={18} />
                    {tx(cat.label)}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Articles Grid */}
          <div className="flex-1">
            {filteredArticles.length === 0 ? (
              <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Book size={48} className="mx-auto mb-4 opacity-50" />
                <p>{tx("No articles found matching your search.")}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredArticles.map(article => {
                  const Icon = article.icon;
                  return (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isDark 
                          ? 'bg-[#111827] border-gray-800 hover:border-cyan-500/50 hover:bg-[#151f2e]' 
                          : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                      }`}
                      data-testid={`help-article-${article.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/10' : 'bg-blue-50'}`}>
                          <Icon size={20} className={isDark ? 'text-cyan-400' : 'text-blue-600'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {tx(article.title)}
                          </h4>
                          <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {article.summary}
                          </p>
                        </div>
                        <ChevronRight size={20} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Contact Support */}
        <div className={`mt-8 p-6 rounded-xl text-center ${
          isDark ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100'
        }`}>
          <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('helpCenter.cantFindAnswer')}
          </p>
          <button
            onClick={() => setShowCallMeModal(true)}
            className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors ${
              isDark ? 'bg-cyan-500 text-white hover:bg-cyan-600' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            data-testid="help-contact-support"
          >
            <Phone size={18} />
            {t('helpCenter.contactSupport')}
          </button>
        </div>
      </div>
      
      {/* Call Me Modal */}
      {showCallMeModal && <CallMeModal />}
    </div>
  );
};

export default TraderHelpCenter;
